/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon, PlayIcon } from "@heroicons/react/24/outline";
import { getHistory, runSpeedTest } from "@/api/speedtest";
import { MonitorAgent } from "@/api/monitor";
import { SpeedTestResult, PaginatedResponse } from "@/types/types";
import { showToast } from "@/components/common/Toast";
import { LOCAL_SOURCE as LOCAL, useSpeedSource } from "@/utils/speedSource";

// An agent whose SSE URL is loopback on the default agent port (8200) is the
// nyrvex server's own embedded agent -- i.e. the same connection as a
// "local" test -- so it isn't offered as a separate source. A tunnelled
// remote agent is loopback too but on another port (e.g. :18500).
function isColocated(url: string): boolean {
  try {
    const u = new URL(url);
    const loopback = ["127.0.0.1", "localhost", "::1", "[::1]"].includes(u.hostname);
    return loopback && u.port === "8200";
  } catch {
    return false;
  }
}

function ago(iso: string | undefined) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

interface SpeedtestWidgetProps {
  agents?: MonitorAgent[];
}

/** Compact one-line summary of the latest speed test, with a Run button. */
export const SpeedtestWidget: React.FC<SpeedtestWidgetProps> = ({ agents = [] }) => {
  const queryClient = useQueryClient();
  const remoteAgents = useMemo(
    () => agents.filter((a) => a.enabled && !isColocated(a.url)),
    [agents],
  );

  const [src, pickSrc] = useSpeedSource();

  const effectiveSrc =
    src === LOCAL || remoteAgents.some((a) => String(a.id) === src) ? src : LOCAL;
  const localName = agents.find((a) => isColocated(a.url))?.name ?? "aeza";
  const srcLabel =
    effectiveSrc === LOCAL
      ? localName
      : (remoteAgents.find((a) => String(a.id) === effectiveSrc)?.name ?? "agent");

  const { data, isLoading } = useQuery({
    queryKey: ["home-speedtest-latest", effectiveSrc],
    queryFn: () =>
      getHistory("all", 1, 40, effectiveSrc) as Promise<PaginatedResponse<SpeedTestResult>>,
    refetchInterval: 60_000,
    retry: false,
  });

  const run = useMutation({
    mutationFn: () =>
      // the same test as the Network overview's Run button
      effectiveSrc !== LOCAL
        ? runSpeedTest({ agentId: Number(effectiveSrc), serverIds: [], enableDownload: true, enableUpload: true })
        : runSpeedTest({ serverIds: ["cloudflare"], enableDownload: true, enableUpload: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["home-speedtest-latest"] });
      queryClient.invalidateQueries({ queryKey: ["net-overview-speed"] });
      queryClient.invalidateQueries({ queryKey: ["speedTestHistory"] });
      showToast("Speed test complete", "success");
    },
    onError: (e: Error) =>
      showToast("Speed test failed", "error", { description: e.message }),
  });

  // full tests only (download-only mirror runs are not comparable), Cloudflare first like the Network page
  const full = (data?.data ?? []).filter((r) => r.downloadSpeed >= 1 && r.uploadSpeed >= 1);
  const pool = full.filter((r) => r.serverName === "Cloudflare").length > 0 ? full.filter((r) => r.serverName === "Cloudflare") : full;
  const latest = pool[0];
  const prev = pool[1];
  const pingMs = latest?.latency ? parseFloat(latest.latency) : NaN;
  const split = (n: number) => (n >= 1000 ? { v: (n / 1000).toFixed(2), u: "Gbps" } : { v: n.toFixed(0), u: "Mbps" });
  const dl = latest ? split(latest.downloadSpeed) : null;
  const ul = latest ? split(latest.uploadSpeed) : null;
  const change =
    latest && prev && prev.downloadSpeed
      ? ((latest.downloadSpeed - prev.downloadSpeed) / prev.downloadSpeed) * 100
      : null;

  return (
    <section className="cc-speed">
      <div className="cc-speed-head">
        <span className="cc-speed-title">Speed test</span>
        {latest && !run.isPending && <span className="cc-speed-ago">{ago(latest.createdAt)}</span>}
      </div>

      {run.isPending ? (
        <div className="cc-speed-note">{`testing ${srcLabel}… ~30s`}</div>
      ) : isLoading ? (
        <div className="cc-speed-note">loading…</div>
      ) : !latest || !dl || !ul ? (
        <div className="cc-speed-note">no tests yet</div>
      ) : (
        <div className="cc-speed-grid">
          <div className="cc-speed-m cc-speed-m--dl">
            <span className="cc-speed-k">Download</span>
            <b>{dl.v}</b>
            <small>
              {dl.u}
              {change !== null && Math.abs(change) >= 3 && (
                <em className={change >= 0 ? "cc-up" : "cc-down"}>{` ${change >= 0 ? "+" : ""}${change.toFixed(0)}%`}</em>
              )}
            </small>
          </div>
          <div className="cc-speed-m">
            <span className="cc-speed-k">Upload</span>
            <b>{ul.v}</b>
            <small>{ul.u}</small>
          </div>
          <div className="cc-speed-m">
            <span className="cc-speed-k">Ping</span>
            <b>{Number.isFinite(pingMs) ? Math.round(pingMs) : "—"}</b>
            <small>ms</small>
          </div>
        </div>
      )}

      <div className="cc-speed-foot">
        {remoteAgents.length > 0 ? (
          <select
            className="gh-agent-select"
            value={effectiveSrc}
            onChange={(e) => pickSrc(e.target.value)}
            aria-label="Speed test source"
          >
            <option value={LOCAL}>{localName}</option>
            {remoteAgents.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="cc-speed-srv" title={latest?.serverName ?? ""}>
            {latest?.serverName ?? srcLabel}
          </span>
        )}
        <button
          type="button"
          className="gh-btn cc-speed-run"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          title={`Run a speed test from ${srcLabel} now`}
        >
          {run.isPending ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
          {run.isPending ? "Testing" : "Run"}
        </button>
      </div>
    </section>
  );
};
